import useOptimisticUpdateFolderEntry from '@/api/mutations/folders/optimistic/useOptimisticUpdateFolderEntry';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import projectKeys from '@/api/queries/project/keyFactory';
import useGetProjectFolderEntries, {
  TFolderEntry,
} from '@/api/queries/project/useGetProjectFolderEntries';
import useGetProjectFolders, {
  TFolder,
} from '@/api/queries/project/useGetProjectFolders';
import { EntryType, GetFolderEntriesInput } from '@/gql/graphql';
import { useDelayedFn } from '@/hooks/useDelayedFn';
import useSearchParamState from '@/hooks/useSearchParamState';
import { DealFileUploadModal } from '@/ui/components/dealFileUploadModal/DealFileUploadModal';
import CreateEditFolderModal from '@/ui/pages/projectPage/components/FilesPanel/components/CreateEditFolderModal';
import { FOLDER_CARD_DROP_TYPE } from '@/ui/pages/projectPage/components/FilesPanel/components/FolderCard';
import RenameFolderEntryModal from '@/ui/pages/projectPage/components/FilesPanel/components/RenameFolderEntryModal';
import { FOLDER_ENTRY_DRAG_TYPE } from '@/ui/pages/projectPage/components/FilesPanel/DraggableFolderEntry';
import FilesPanelLightbox from '@/ui/pages/projectPage/components/FilesPanel/FilesPanelLightbox';
import FolderEntriesList, {
  MULTIPLE_ENTRIES_DRAG_ID,
} from '@/ui/pages/projectPage/components/FilesPanel/FolderEntriesList';
import FoldersList from '@/ui/pages/projectPage/components/FilesPanel/FoldersList';
import FoldersTreeView, {
  TREE_NODE_DROP_TYPE,
} from '@/ui/pages/projectPage/components/FilesPanel/FoldersTreeView';
import { pointerWithin } from '@dnd-kit/core';
import {
  BREADCRUMB_DROP_TYPE,
  BreadcrumbItem,
  ConditionalWrapper,
  InputIcon,
  ManageableItemsList,
  Tooltip,
  useToastContext,
} from '@payaca/ambrosia';
import { DndContext } from '@payaca/dnd';

import { useDebounce } from '@payaca/hooks';
import { checkUnreachable } from '@payaca/utilities/guards';
import { useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { FC, useEffect, useReducer } from 'react';
import { useRouteMatch } from 'react-router-dom';
import DeleteFilesModal from './components/DeleteFilesModal';
import FilesPanelHeader from './components/FilesPanelHeader';
import ShareFilesModal from './components/ShareFilesModal';
import UnshareFilesModal from './components/UnshareFilesModal';

export type TProps = {
  projectId: string;
  projectNumericalId: number;
};

const FilesPanel: FC<TProps> = (props) => {
  const { projectId, projectNumericalId } = props;

  /**
   * state
   */
  const accountHasAiFeature = useMyAccountHasFeature('useAi');
  const { pushSuccess } = useToastContext();
  const { path } = useRouteMatch();
  const workingFolderRoute = useRouteMatch<{
    workingFolderId: string;
  }>(`${path}/:workingFolderId`);

  const [filesPanelState, dispatchFilesPanelAction] = useReducer(
    filesPanelReducer,
    {
      selectedEntries: [],
      isTreeViewOpen: true,
      expandedTreeNodeIds: new Set<string>(),
      getFolderEntriesInput: {
        searchTerm: '',
        includeDescendantEntries: false,
        parentFolderId: undefined,
        entryTypes: undefined,
      },
      openModal: null,
    }
  );

  const {
    selectedEntries,
    isTreeViewOpen,
    expandedTreeNodeIds,
    getFolderEntriesInput,
    openModal,
    folderEntriesToDelete,
    folderEntriesToShare,
    folderEntriesToUnshare,
    folderEntryToRename,
    lightboxViewingFolderEntryId,
  } = filesPanelState;

  const [searchTerm, setSearchTerm] = useSearchParamState(
    'filesSearchTerm',
    ''
  );

  useDebounce(
    () => {
      dispatchFilesPanelAction({
        type: 'setGetFolderEntriesInput',
        payload: {
          partialGetFolderEntriesInput: {
            searchTerm,
            includeDescendantEntries: searchTerm.length > 0,
          },
        },
      });
    },
    500,
    [searchTerm]
  );

  const [openTreeViewWithDelay, cancelOpenTreeViewWithDelay] = useDelayedFn(
    () => {
      dispatchFilesPanelAction({ type: 'openTreeView' });
    },
    500
  );

  const [expandTreeNodeWithDelay, cancelExpandTreeNodeWithDelay] = useDelayedFn(
    (itemId: string) => {
      dispatchFilesPanelAction({
        type: 'expandTreeViewItems',
        payload: { treeItemIds: new Set([itemId]) },
      });
    },
    500
  );

  /**
   * queries
   */
  const queryClient = useQueryClient();

  const {
    projectFolders,
    projectRootFolder,
    isLoading: isLoadingProjectFolders,
  } = useGetProjectFolders(projectId);

  const workingFolder = projectFolders?.find(
    (i) => i.id === workingFolderRoute?.params.workingFolderId
  );

  // When parentFolderId is undefined, use root folder ID to avoid showing all entries
  const computedGetFolderEntriesInputOpts = {
    input: {
      ...getFolderEntriesInput,
      parentFolderId: workingFolder?.id ?? projectRootFolder?.id,
    },
    enabled: !!projectId && !isLoadingProjectFolders,
  };

  const {
    projectFolderEntries,
    dataUpdatedAt: lastUpdatedFolderEntriesData,
    totalCount: totalFolderEntriesCount,
    hasNextPage,
    fetchNextPage,
    isRefetching: isRefetchingFolderEntries,
    isFetching: isFetchingProjectFolderEntries,
  } = useGetProjectFolderEntries(projectId, computedGetFolderEntriesInputOpts);

  /**
   * mutations
   */
  const { mutate: updateFolderEntryMutation } = useOptimisticUpdateFolderEntry({
    projectId,
  });

  /**
   * effects
   */
  useEffect(() => {
    dispatchFilesPanelAction({
      type: 'unselectAllEntries',
    });
  }, [projectFolderEntries]);

  useEffect(() => {
    const allFolderIdsThatShouldBeOpen = getFolderAncestorsIds({
      childId: workingFolder?.id,
      folders: projectFolders,
    });

    dispatchFilesPanelAction({
      type: 'expandTreeViewItems',
      payload: { treeItemIds: allFolderIdsThatShouldBeOpen },
    });
  }, [workingFolder, projectFolders]);

  /**
   * callbacks
   */
  const handleReparentFolderEntries = async (
    entryIds: string[],
    newParentFolderId: string
  ) => {
    if (entryIds.length === 0) {
      return;
    }

    await Promise.allSettled(
      entryIds.map((entryId) => {
        return updateFolderEntryMutation({
          id: entryId,
          parentFolderId: newParentFolderId,
        });
      })
    );

    const newParentFolder = projectFolders?.find(
      (i) => i.id === newParentFolderId
    );

    if (newParentFolder) {
      const newParentFolderName = newParentFolder.isRoot
        ? 'Files'
        : newParentFolder.name;

      pushSuccess(
        `Moved ${
          entryIds.length > 1 ? 'files' : 'file'
        } to ${newParentFolderName}`
      );
    }
  };

  const breadcrumbItems = useGenerateBreadcrumbItems(projectId, {
    workingFolder,
  });

  return (
    <>
      <DndContext
        collisionDetection={pointerWithin}
        onDragOver={(event) => {
          if (event.over?.id === 'files-tree') {
            openTreeViewWithDelay();
          } else if (event.over?.data.current?.type === TREE_NODE_DROP_TYPE) {
            if (
              !event.over.data.current ||
              !event.over.data.current.folderId ||
              !event.over.data.current.hasChildren
            ) {
              return;
            }

            expandTreeNodeWithDelay(event.over.data.current.folderId as string);
          } else {
            cancelOpenTreeViewWithDelay();
            cancelExpandTreeNodeWithDelay();
          }
        }}
        onDragEnd={(event) => {
          if (
            event.active.data.current?.type === FOLDER_ENTRY_DRAG_TYPE &&
            (event.over?.data.current?.type === FOLDER_CARD_DROP_TYPE ||
              event.over?.data.current?.type === TREE_NODE_DROP_TYPE ||
              event.over?.data.current?.type === BREADCRUMB_DROP_TYPE) &&
            typeof event.over?.data.current?.folderId === 'string' &&
            typeof event.active?.data.current?.folderEntryId === 'string'
          ) {
            const isMultiDrag = event.active.id === MULTIPLE_ENTRIES_DRAG_ID;

            void handleReparentFolderEntries(
              isMultiDrag
                ? selectedEntries.map((i) => i.id)
                : [event.active.data.current.folderEntryId],
              event.over.data.current.folderId
            );
          }

          cancelOpenTreeViewWithDelay();
          cancelExpandTreeNodeWithDelay();
        }}
      >
        <div className="flex flex-col md:flex-row gap-4 w-full">
          {isTreeViewOpen && (
            <FoldersTreeView
              projectId={projectId}
              expandedTreeNodeIds={expandedTreeNodeIds}
              workingFolder={workingFolder}
              onOpenCreateFolderModal={() =>
                dispatchFilesPanelAction({
                  type: 'openModal',
                  payload: { modalType: 'createEditFolder' },
                })
              }
              onExpandTreeNode={(treeItemId) =>
                dispatchFilesPanelAction({
                  type: 'expandTreeViewItems',
                  payload: { treeItemIds: new Set([treeItemId]) },
                })
              }
              onCollapseTreeNodes={(treeItemIds) =>
                dispatchFilesPanelAction({
                  type: 'collapseTreeViewItems',
                  payload: { treeItemIds },
                })
              }
            />
          )}

          <ManageableItemsList className="flex-1">
            <div className="space-y-4 p-4">
              {/* Header*/}
              <FilesPanelHeader
                isTreeViewOpen={isTreeViewOpen}
                breadcrumbItems={breadcrumbItems}
                lastUpdatedFolderEntriesData={lastUpdatedFolderEntriesData}
                isRefetchingFolderEntries={isRefetchingFolderEntries}
                projectId={projectId}
                workingFolder={workingFolder}
                onAddFile={() =>
                  dispatchFilesPanelAction({
                    type: 'openModal',
                    payload: { modalType: 'uploadFile' },
                  })
                }
                onToggleTreeView={() =>
                  dispatchFilesPanelAction({ type: 'toggleTreeView' })
                }
              />
              <div className="flex items-stretch justify-end gap-2">
                <ConditionalWrapper
                  Wrapper={Tooltip}
                  condition={accountHasAiFeature}
                  wrapperProps={{
                    tooltipContent: 'Search by what you see in the images',
                    className: 'mr-auto w-full',
                    placement: 'top-start',
                  }}
                >
                  <ManageableItemsList.ActionBar.SearchInput
                    value={searchTerm}
                    type="search"
                    leadingElement={
                      accountHasAiFeature ? (
                        <InputIcon name="star-06.3" />
                      ) : undefined
                    }
                    placeholder={
                      accountHasAiFeature ? 'Search with AI' : 'Search'
                    }
                    onChange={(value) => setSearchTerm(value)}
                  />
                </ConditionalWrapper>

                <ManageableItemsList.ActionBar.BasicFilter
                  options={filterOptions}
                  value={getFolderEntriesInput.entryTypes || []}
                  onChange={(newValue) =>
                    dispatchFilesPanelAction({
                      type: 'setGetFolderEntriesInput',
                      payload: {
                        partialGetFolderEntriesInput: {
                          entryTypes:
                            newValue.length > 0
                              ? (newValue as EntryType[])
                              : undefined,
                        },
                      },
                    })
                  }
                />
              </div>

              <hr />

              {/*Folders*/}
              <FoldersList
                projectId={projectId}
                workingFolder={workingFolder}
              />

              <FolderEntriesList
                // Avoid re-layout animations when moving between folders where
                // there is no commonality between the folder entries
                key={workingFolder?.id || 'root-folder'}
                projectId={projectId}
                isFetchingProjectFolderEntries={isFetchingProjectFolderEntries}
                projectFolderEntries={projectFolderEntries}
                selectedEntries={selectedEntries}
                onViewFolderEntry={(folderEntryId) =>
                  dispatchFilesPanelAction({
                    type: 'viewFolderEntryInLightbox',
                    payload: { folderEntryId },
                  })
                }
                selectFolderEntry={(folderEntry) =>
                  dispatchFilesPanelAction({
                    type: 'selectEntry',
                    payload: { folderEntry },
                  })
                }
                unselectFolderEntry={(folderEntry) =>
                  dispatchFilesPanelAction({
                    type: 'unselectEntry',
                    payload: { folderEntry },
                  })
                }
                onDelete={(selectedFolderEntry) => {
                  dispatchFilesPanelAction({
                    type: 'openModal',
                    payload: {
                      modalType: 'deleteFiles',
                      entriesToDelete: [selectedFolderEntry],
                    },
                  });
                }}
                onShare={(selectedFolderEntry) => {
                  dispatchFilesPanelAction({
                    type: 'openModal',
                    payload: {
                      modalType: 'shareFiles',
                      entriesToShare: [selectedFolderEntry],
                    },
                  });
                }}
                onUnshare={(selectedFolderEntry) => {
                  dispatchFilesPanelAction({
                    type: 'openModal',
                    payload: {
                      modalType: 'unshareFiles',
                      entriesToUnshare: [selectedFolderEntry],
                    },
                  });
                }}
                onRename={(selectedFolderEntry) => {
                  dispatchFilesPanelAction({
                    type: 'openModal',
                    payload: {
                      modalType: 'renameFolderEntry',
                      entryToRename: selectedFolderEntry,
                    },
                  });
                }}
              />
            </div>

            <ManageableItemsList.InfinitePaginationBar
              count={projectFolderEntries?.length ?? 0}
              totalCount={totalFolderEntriesCount}
              onLoadMore={fetchNextPage}
              hasNextPage={
                hasNextPage ?? isFetchingProjectFolderEntries ?? false
              }
            />

            <ManageableItemsList.StaticGlobalActionBar
              isOpen={selectedEntries.length > 0 && openModal === null}
              onClose={() =>
                dispatchFilesPanelAction({ type: 'unselectAllEntries' })
              }
              numberOfSelectedItems={selectedEntries.length}
              actions={[
                {
                  label: 'Share',
                  onClick: () =>
                    dispatchFilesPanelAction({
                      type: 'openModal',
                      payload: {
                        modalType: 'shareFiles',
                        entriesToShare: selectedEntries,
                      },
                    }),
                  icon: 'share-03',
                },
              ]}
            />
          </ManageableItemsList>
        </div>
      </DndContext>

      <DealFileUploadModal
        projectId={projectId}
        parentFolderId={workingFolder?.id}
        isOpen={openModal === 'uploadFile'}
        onClose={() => dispatchFilesPanelAction({ type: 'closeModal' })}
        onUploadSuccess={() => {
          dispatchFilesPanelAction({ type: 'closeModal' });
          void queryClient.invalidateQueries({
            queryKey: projectKeys.folders(projectId),
          });
        }}
      />

      <CreateEditFolderModal
        projectId={projectId}
        isOpen={openModal === 'createEditFolder'}
        workingFolder={workingFolder || projectRootFolder}
        onClose={() => dispatchFilesPanelAction({ type: 'closeModal' })}
      />

      <FilesPanelLightbox
        projectId={projectId}
        folderEntryId={lightboxViewingFolderEntryId}
        onChange={(newFolderEntryId) =>
          dispatchFilesPanelAction({
            type: 'viewFolderEntryInLightbox',
            payload: { folderEntryId: newFolderEntryId },
          })
        }
        isOpen={!!lightboxViewingFolderEntryId}
        onClose={() =>
          dispatchFilesPanelAction({
            type: 'closeLightbox',
          })
        }
        getFolderEntriesInputOpts={computedGetFolderEntriesInputOpts}
      />

      <DeleteFilesModal
        isOpen={openModal === 'deleteFiles'}
        onClose={() => dispatchFilesPanelAction({ type: 'closeModal' })}
        folderEntries={folderEntriesToDelete || []}
        projectId={projectId}
      />
      <ShareFilesModal
        isOpen={openModal === 'shareFiles'}
        onClose={() => dispatchFilesPanelAction({ type: 'closeModal' })}
        folderEntries={folderEntriesToShare || []}
        projectId={projectId}
      />
      <UnshareFilesModal
        isOpen={openModal === 'unshareFiles'}
        onClose={() => dispatchFilesPanelAction({ type: 'closeModal' })}
        folderEntries={folderEntriesToUnshare || []}
        projectId={projectId}
      />
      <RenameFolderEntryModal
        projectId={projectId}
        isOpen={openModal === 'renameFolderEntry'}
        onClose={() => dispatchFilesPanelAction({ type: 'closeModal' })}
        folderEntryToRename={folderEntryToRename}
      />
    </>
  );
};

type FilesPanelState = {
  selectedEntries: TFolderEntry[];
  isTreeViewOpen: boolean;
  expandedTreeNodeIds: Set<string>;
  getFolderEntriesInput: GetFolderEntriesInput;
  openModal:
    | 'createEditFolder'
    | 'uploadFile'
    | 'shareFiles'
    | 'unshareFiles'
    | 'deleteFiles'
    | 'renameFolderEntry'
    | null;
  folderEntriesToDelete?: TFolderEntry[];
  folderEntriesToShare?: TFolderEntry[];
  folderEntriesToUnshare?: TFolderEntry[];
  folderEntryToRename?: TFolderEntry;
  lightboxViewingFolderEntryId?: string;
};

type FilesPanelAction =
  | {
      type: 'selectEntry';
      payload: { folderEntry: TFolderEntry };
    }
  | {
      type: 'unselectEntry';
      payload: { folderEntry: TFolderEntry };
    }
  | {
      type: 'unselectAllEntries';
    }
  | {
      type: 'openTreeView';
    }
  | {
      type: 'toggleTreeView';
    }
  | {
      type: 'expandTreeViewItems';
      payload: { treeItemIds: Set<string> };
    }
  | {
      type: 'collapseTreeViewItems';
      payload: { treeItemIds: Set<string> };
    }
  | {
      type: 'setGetFolderEntriesInput';
      payload: {
        partialGetFolderEntriesInput: Partial<
          Omit<GetFolderEntriesInput, 'parentFolderId'>
        >;
      };
    }
  | {
      type: 'openModal';
      payload:
        | { modalType: 'createEditFolder' }
        | { modalType: 'uploadFile' }
        | {
            modalType: 'shareFiles';
            entriesToShare: TFolderEntry[];
          }
        | {
            modalType: 'unshareFiles';
            entriesToUnshare: TFolderEntry[];
          }
        | {
            modalType: 'deleteFiles';
            entriesToDelete: TFolderEntry[];
          }
        | {
            modalType: 'renameFolderEntry';
            entryToRename: TFolderEntry;
          };
    }
  | {
      type: 'closeModal';
    }
  | {
      type: 'viewFolderEntryInLightbox';
      payload: { folderEntryId?: string };
    }
  | {
      type: 'closeLightbox';
    };

const filesPanelReducer = (
  baseState: FilesPanelState,
  action: FilesPanelAction
) => {
  return produce(baseState, (draftState) => {
    const type = action.type;

    switch (type) {
      case 'selectEntry': {
        if (
          draftState.selectedEntries.some(
            (selectedEntry) =>
              selectedEntry.id === action.payload.folderEntry.id
          )
        ) {
          break;
        }

        draftState.selectedEntries = [
          ...draftState.selectedEntries,
          action.payload.folderEntry,
        ];

        break;
      }
      case 'unselectEntry':
        draftState.selectedEntries = draftState.selectedEntries.filter(
          (entry) => entry.id !== action.payload.folderEntry.id
        );
        break;
      case 'unselectAllEntries':
        draftState.selectedEntries = [];
        break;
      case 'openTreeView':
        draftState.isTreeViewOpen = true;
        break;
      case 'toggleTreeView':
        draftState.isTreeViewOpen = !draftState.isTreeViewOpen;
        break;
      case 'expandTreeViewItems': {
        draftState.expandedTreeNodeIds = draftState.expandedTreeNodeIds.union(
          action.payload.treeItemIds
        );

        break;
      }
      case 'collapseTreeViewItems': {
        draftState.expandedTreeNodeIds =
          draftState.expandedTreeNodeIds.difference(action.payload.treeItemIds);

        break;
      }
      case 'setGetFolderEntriesInput':
        draftState.getFolderEntriesInput = {
          ...draftState.getFolderEntriesInput,
          ...action.payload.partialGetFolderEntriesInput,
        };

        break;
      case 'openModal':
        draftState.openModal = action.payload.modalType;

        if (action.payload.modalType === 'deleteFiles') {
          draftState.folderEntriesToDelete = action.payload.entriesToDelete;
        } else if (action.payload.modalType === 'shareFiles') {
          draftState.folderEntriesToShare = action.payload.entriesToShare;
        } else if (action.payload.modalType === 'unshareFiles') {
          draftState.folderEntriesToUnshare = action.payload.entriesToUnshare;
        } else if (action.payload.modalType === 'renameFolderEntry') {
          draftState.folderEntryToRename = action.payload.entryToRename;
        }
        break;
      case 'closeModal':
        draftState.openModal = null;
        break;
      case 'viewFolderEntryInLightbox':
        draftState.lightboxViewingFolderEntryId = action.payload.folderEntryId;
        break;
      case 'closeLightbox':
        draftState.lightboxViewingFolderEntryId = undefined;
        break;
      default:
        checkUnreachable(type);
        break;
    }
  });
};

const filterOptions = [
  {
    label: 'Image',
    value: 'IMAGE',
  },
  {
    label: 'Video',
    value: 'VIDEO',
  },
  {
    label: 'PDF',
    value: 'PDF',
  },
  {
    label: 'Link',
    value: 'URL',
  },
  {
    label: 'Document',
    value: 'DOCUMENT',
  },
  {
    label: 'Form',
    value: 'FORM',
  },
  {
    label: 'Other',
    value: 'OTHER',
  },
];

const useGenerateBreadcrumbItems = (
  projectId: string,
  args: {
    workingFolder?: TFolder;
  }
) => {
  const { url } = useRouteMatch();
  const { projectFolders, projectRootFolder } = useGetProjectFolders(projectId);

  const toItem = (folder?: TFolder, label?: string): BreadcrumbItem => ({
    label: label ?? folder?.name ?? 'Files',
    to: folder?.isRoot ? url : `${url}${folder ? `/${folder.id}` : ''}`,
    data: { folderId: folder?.id },
  });

  const byId = new Map<string, TFolder>(
    (projectFolders ?? []).map((f) => [f.id, f])
  );

  const path: TFolder[] = [];
  let current = args.workingFolder;
  while (current && !current.isRoot) {
    path.push(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }

  // root first, then the path
  return [
    toItem(projectRootFolder, 'Files'),
    ...path.reverse().map((folder) => toItem(folder)),
  ];
};

const getFolderAncestorsIds = (args: {
  childId?: string;
  folders?: TFolder[];
}) => {
  const ancestors = new Set<string>();

  if (!args.childId || !args.folders) {
    return ancestors;
  }

  const { childId, folders } = args;

  const byId = new Map<string, TFolder>(folders.map((f) => [f.id, f]));

  let current = byId.get(childId);
  while (current?.parentId) {
    ancestors.add(current.parentId);
    current = byId.get(current.parentId);

    if (!current) {
      break;
    }
  }

  return ancestors;
};

export default FilesPanel;
